import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import type { CustomScriptTemplate, MenuOption } from "./Interfaces"
import { TemplateCard } from "./TemplateCard"
import { MarketPlaceIntegrationDialog } from "./components/MarketPlaceIntegrationDialog"

/**
 * Container for the MarketPlace page
 *
 * @param {Script} ScriptGroupList
 * @return {ReactElement}
 */
export function Container({ template }: Readonly<{ template: CustomScriptTemplate }>): React.ReactElement {
  const { name, uuid, isInUse, script_uuid, logo } = template

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve script", permissions.konan, flattenedKonanPermissions)

  const { t } = useTranslation("marketplace", { keyPrefix: "container" })

  const { id: projectId } = useParams()

  const [isBuiltinScriptDialogOpen, setIsBuiltinScriptDialogOpen] = useState<{
    isOpen: boolean
    isEditMode: boolean
  }>({
    isOpen: false,
    isEditMode: false,
  })

  const [isDeleteScriptDialogOpen, setIsDeleteScriptDialogOpen] = useState<boolean>(false)

  const DeleteScriptMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      script_uuid: string
    }
  >(KonanAPI.DeleteScript, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.disconnectSuccess"), { severity: "success" })

      queryClient.invalidateQueries(["scripts", projectId])
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.disconnectError"), { severity: "error" })
    },
  })

  return (
    <Fragment>
      {isBuiltinScriptDialogOpen.isOpen && (
        <MarketPlaceIntegrationDialog
          isOpen
          onClose={() => setIsBuiltinScriptDialogOpen({ isOpen: false, isEditMode: false })}
          scriptInfo={{ name: name, uuid: uuid, scriptId: script_uuid, logo: logo as string }}
          isNewScriptTriggered={!isInUse}
          renderView="scripts"
          openInEditMode={isBuiltinScriptDialogOpen.isEditMode}
          canShowDetails={canShowDetails}
        />
      )}

      {isDeleteScriptDialogOpen && (
        <BaseSimpleDialog
          isLoading={DeleteScriptMutation.isLoading}
          onAccept={async () => {
            await DeleteScriptMutation.mutateAsync({
              project_uuid: projectId as string,
              script_uuid: script_uuid as string,
            })
            setIsBuiltinScriptDialogOpen({ isOpen: false, isEditMode: false })
          }}
          mode="script-deletion"
          name={name}
          open
          onClose={() => setIsDeleteScriptDialogOpen(false)}
        />
      )}

      <TemplateCard
        MenuOptions={
          isInUse
            ? ([
                {
                  title: canShowDetails ? t("edit") : t("test"),
                  variant: "base",
                  onClick: () => {
                    setIsBuiltinScriptDialogOpen({ isOpen: true, isEditMode: canShowDetails })
                  },
                },
                canShowDetails && {
                  title: t("disconnect"),
                  variant: "negative",
                  onClick: () => setIsDeleteScriptDialogOpen(true),
                },
              ].filter(Boolean) as MenuOption[])
            : undefined
        }
        template={template}
        canShowDetails={canShowDetails}
      />
    </Fragment>
  )
}
